import { clientCounts, countsLine } from '@boViews/shared/clientCounts';
import { formatList, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import { faArrowTurnDownRight, faCircleExclamation, faLaptop, faLightbulb, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import { InputCheckBox } from '@sharedComponents/InputCheckBox';
import Loader from '@sharedComponents/Loader';
import Tag from '@sharedComponents/Tag';
import ToggleSwitch from '@sharedComponents/ToggleSwitch';
import Typography from '@sharedComponents/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPlatformTypeLabel } from '../../helpers';
import { RESTRICTED } from '../accessRules';
import { overriddenLanguages, rowPayload, subTargetEntries, targetKey } from '../overrideRules';
import RowValues from '../RowValues';

import * as style from './style.module.css';

const INDENTS = [null, style.indent1, style.indent2];
const NO_ANSWER = { attempt: -1, client: null, failed: false };

const noClient = async () => null;
const noAddition = async () => ({ added: [], failed: [] });

// One line of the tree, in columns: checkbox, name over its details, state. The line starts further right at
// each level, below level 1 with an arrow. The checkbox and the name of a target that can be ticked form its
// label, so that a click on the name ticks it: their cells are spans, as a label holds phrasing content only.
const TreeRow = ({ level, isPlatform, name, details, checkbox = null, selectable = false, state, muted = false, className = '' }) => {
    const target = (
        <>
            <span className={`${style.checkCell} ${INDENTS[level - 1] ?? ''}`}>{checkbox}</span>
            <span className={style.nameCell}>
                {level > 1 && <FontAwesomeIcon icon={faArrowTurnDownRight} className={style.mutedIcon} />}
                {isPlatform && <FontAwesomeIcon icon={faLaptop} className={style.mutedIcon} />}
                <span className={style.details}>
                    <Typography span fontWeight={isPlatform ? 'regular' : 'medium'} color={muted ? 'neutral-tertiary' : 'neutral-primary'}>
                        {name}
                    </Typography>
                    <Typography span fontSize="s" color="neutral-tertiary">
                        {details}
                    </Typography>
                </span>
            </span>
        </>
    );
    return (
        <FlexBox role="listitem" alignItems="center" gap="xs" className={`${style.row} ${className}`}>
            {selectable ? (
                <label className={`${style.target} ${style.selectable}`}>{target}</label>
            ) : (
                <FlexBox alignItems="center" gap="xs" flexGrow={1} flexShrink={1} flexBasis={0} className={style.target}>
                    {target}
                </FlexBox>
            )}
            <FlexBox justifyContent="flex-end" alignItems="center" className={style.stateCell}>
                {state}
            </FlexBox>
        </FlexBox>
    );
};

/**
 * Body of the "Sub-clients and platforms" modal of the client row `row`: its client with its sub-clients and
 * platforms, loaded by `loadClient(uuid)`, as a tree where the sub-clients and platforms covered by a row can
 * be ticked to get their own row, which fully prevails over that of the client. A null client shows as a
 * client without sub-client nor platform. The new rows get a one-off copy of the client row's values, unless
 * the user turns the option off: they then start from the collection.
 * `onAddTargets(targets, payload)` resolves to `{ added, failed }`, two lists of `kind:uuid` keys, and must
 * not reject. Everything added closes the modal, unless its body was unmounted meanwhile (another modal
 * opened); otherwise it stays open on the targets left. Its props stay those of its opening, so it keeps the
 * rows it added itself.
 */
const SubTargetsModal = ({
    row,
    overrides = [],
    visibility,
    languages = [],
    loadClient = noClient,
    onAddTargets = noAddition,
    onClose = () => {},
}) => {
    const { t, i18n } = useTranslation('bo');
    const [attempt, setAttempt] = useState(0);
    // Last answer of loadClient, tagged with its attempt: while another attempt loads, nothing of it is shown.
    const [answer, setAnswer] = useState(NO_ANSWER);
    const [checked, setChecked] = useState([]);
    // Values of the rows added by this modal, by target key.
    const [added, setAdded] = useState({});
    const [copyValues, setCopyValues] = useState(true);
    const [sending, setSending] = useState(false);
    const [failedNames, setFailedNames] = useState([]);
    const mounted = useRef(true);

    const { name } = row.client;
    const clientUuid = row.client.uuid;
    const restricted = visibility === RESTRICTED;
    const total = languages.length;
    const loading = answer.attempt !== attempt;
    const loaded = !loading && !answer.failed;
    const entries = loaded && answer.client ? subTargetEntries(answer.client, overrides) : [];
    const empty = loaded && entries.length <= 1;
    const selected = entries
        .filter(
            (entry) =>
                entry.state === 'covered' && !Object.hasOwn(added, targetKey(entry.target)) && checked.includes(targetKey(entry.target)),
        )
        .map((entry) => entry.target);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        let current = true;
        loadClient(clientUuid).then(
            (client) => {
                if (current) setAnswer({ attempt, client, failed: false });
            },
            () => {
                if (current) setAnswer({ ...NO_ANSWER, attempt, failed: true });
            },
        );
        return () => {
            current = false;
        };
    }, [attempt, clientUuid, loadClient]);

    const toggle = (key) => setChecked((current) => (current.includes(key) ? current.filter((other) => other !== key) : [...current, key]));

    const add = async () => {
        const targets = selected;
        const payload = copyValues ? rowPayload(row) : {};
        setSending(true);
        setFailedNames([]);
        const outcome = await onAddTargets(targets, payload);
        if (!mounted.current) return;
        // The modal stays mounted, faded out, once closed: the add button is left disabled.
        if (outcome.failed.length === 0) {
            onClose();
            return;
        }
        setAdded((current) => ({ ...current, ...Object.fromEntries(outcome.added.map((key) => [key, payload])) }));
        setChecked((current) => current.filter((key) => !outcome.added.includes(key)));
        setFailedNames(targets.filter((target) => outcome.failed.includes(targetKey(target))).map((target) => target.name));
        setSending(false);
    };

    const overriddenCount = (text) => overriddenLanguages(text).length;

    // Summary of a target's own row: its overridden texts, whether it hides the collection and, for a row of
    // `overrides` in a restricted collection, whether it gives no access; the rows added by this modal give access.
    const summaryOf = (values, fromOverrides) => {
        const titleCount = overriddenCount(values.title);
        const descriptionCount = overriddenCount(values.description);
        return [
            titleCount > 0 && t('title overridden {{overridden}}/{{total}}', { overridden: titleCount, total }),
            descriptionCount > 0 && t('description overridden {{overridden}}/{{total}}', { overridden: descriptionCount, total }),
            values.visible === false && t('hidden'),
            restricted && fromOverrides && values.visible !== true && t('no access'),
        ].filter(Boolean);
    };

    // Details of an entry: the type of a platform, or the counts of a client (see countsLine).
    const detailsOf = ({ target, entity }) =>
        target.kind === 'platform' ? getPlatformTypeLabel(t, entity.type) : countsLine(t, clientCounts(entity));

    const lineOf = (entry) => {
        const key = targetKey(entry.target);
        const isPlatform = entry.target.kind === 'platform';
        const label = t('Override {{name}} separately', { name: entry.target.name });
        if (entry.state === 'source') {
            return (
                <TreeRow
                    key={key}
                    level={entry.level}
                    isPlatform={isPlatform}
                    name={entry.target.name}
                    details={detailsOf(entry)}
                    state={<Tag color="lavender">{t('Current row')}</Tag>}
                    className={style.source}
                />
            );
        }
        const own = entry.state === 'own';
        const values = own ? entry.row : added[key];
        if (values) {
            return (
                <TreeRow
                    key={key}
                    level={entry.level}
                    isPlatform={isPlatform}
                    name={entry.target.name}
                    details={[detailsOf(entry), ...summaryOf(values, own)].join(' · ')}
                    checkbox={<InputCheckBox checked disabled ariaLabel={label} />}
                    state={<Tag color="neutral">{t('Already overridden')}</Tag>}
                    muted
                />
            );
        }
        const isChecked = checked.includes(key);
        return (
            <TreeRow
                key={key}
                level={entry.level}
                isPlatform={isPlatform}
                name={entry.target.name}
                details={detailsOf(entry)}
                checkbox={<InputCheckBox checked={isChecked} disabled={sending} ariaLabel={label} onChange={() => toggle(key)} />}
                selectable
                state={
                    <Typography fontSize="s" color="neutral-tertiary">
                        {isPlatform
                            ? t('Covered by {{name}}', { name: entry.coveredBy })
                            : t('Covered by the row of {{name}}', { name: entry.coveredBy })}
                    </Typography>
                }
                className={isChecked ? style.checked : ''}
            />
        );
    };

    let message = null;
    if (loading) {
        message = (
            <FlexBox flexDirection="column" alignItems="center" gap="xs">
                <Loader />
                <Typography color="neutral-secondary">{t('Loading the sub-clients and platforms of {{name}}…', { name })}</Typography>
            </FlexBox>
        );
    } else if (answer.failed) {
        message = (
            <Typography color="error-primary">{t('The sub-clients and platforms of {{name}} could not be loaded.', { name })}</Typography>
        );
    } else if (empty) {
        message = <Typography color="neutral-secondary">{t('{{name}} has no active sub-client or platform.', { name })}</Typography>;
    }

    let note = t('One-off copy: a later change of {{name}} will not be carried over to the added rows.', { name });
    if (!copyValues) {
        note = restricted
            ? t('The added rows start from the collection (all "Inherited") and are authorized.')
            : t('The added rows start from the collection (all "Inherited") and are visible.');
    }
    const copyLabel = t('Reuse the values of {{name}}', { name });

    // The body holds every part that comes and goes, so that the footer and its focus are kept.
    return (
        <FlexBox flexDirection="column" gap="m">
            <FlexBox flexDirection="column" gap="m">
                {message && (
                    <FlexBox role="status" aria-live="polite" flexDirection="column" alignItems="flex-start">
                        {message}
                    </FlexBox>
                )}
                {!loading && answer.failed && (
                    <FlexBox>
                        <Button outline label={t('Try again')} action={() => setAttempt((current) => current + 1)} />
                    </FlexBox>
                )}
                {loaded && !empty && (
                    <>
                        <Typography color="neutral-secondary">
                            {t(
                                'Tick the sub-clients and platforms to override separately. Each one gets its own row, which fully prevails over the one of {{name}}.',
                                { name },
                            )}
                        </Typography>
                        <FlexBox role="list" flexDirection="column" gap="xxs" className={style.tree}>
                            {entries.map(lineOf)}
                        </FlexBox>
                        <RowValues
                            row={row}
                            total={total}
                            withDisplay={!restricted}
                            header={
                                <FlexBox alignItems="center" gap="s">
                                    <ToggleSwitch
                                        checked={copyValues}
                                        onChange={(event) => setCopyValues(event.target.checked)}
                                        disabled={sending}
                                        ariaLabel={copyLabel}
                                    />
                                    <Typography fontWeight="medium">{copyLabel}</Typography>
                                </FlexBox>
                            }
                        >
                            <FlexBox alignItems="baseline" gap="xs">
                                <FontAwesomeIcon icon={faLightbulb} className={style.mutedIcon} />
                                <Typography fontSize="s" color="neutral-secondary">
                                    {note}
                                </Typography>
                            </FlexBox>
                        </RowValues>
                    </>
                )}
                {failedNames.length > 0 && (
                    <FlexBox role="alert" flexDirection="column">
                        <InformationMessage
                            status="error"
                            icon={faCircleExclamation}
                            text={t('These rows could not be added: {{targets}}. Please try again.', {
                                targets: formatList(failedNames, uiLocale(i18n)),
                            })}
                        />
                    </FlexBox>
                )}
            </FlexBox>
            <FlexBox justifyContent="space-between" alignItems="center" gap="s" flexWrap="wrap" className={style.footer}>
                <Typography fontSize="s" color="neutral-secondary">
                    {t('{{count}} target(s) selected', { count: selected.length })}
                </Typography>
                <FlexBox gap="s" flexWrap="wrap">
                    <Button outline label={t('Cancel')} disabled={sending} action={onClose} />
                    <Button
                        icon={faPlus}
                        label={t('Add {{count}} row(s)', { count: selected.length })}
                        disabled={selected.length === 0 || sending}
                        action={add}
                    />
                </FlexBox>
            </FlexBox>
        </FlexBox>
    );
};

export default SubTargetsModal;
